import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ContactSection = styled.section`
  padding: 120px ${({ theme }) => theme.spacing.lg};
  background: linear-gradient(to bottom, #ffffff 0%, #f8fafc 50%, #e0f2fe 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 10%;
    right: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(0, 168, 168, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15%;
    left: -5%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(0, 102, 204, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 80px ${({ theme }) => theme.spacing.md};
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  animation: ${fadeInUp} 0.6s ease-out;
`;

const SectionBadge = styled.div`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: rgba(0, 102, 204, 0.1);
  border: 1px solid rgba(0, 102, 204, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: #0066CC;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h2`
  color: #1e293b;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  
  span {
    background: linear-gradient(135deg, #0066CC 0%, #00A8A8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const SectionDescription = styled.p`
  color: #64748b;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  max-width: 600px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 102, 204, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: 
    0 4px 6px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  transition: all ${({ theme }) => theme.transitions.base};
  opacity: ${({ $inView }) => ($inView ? 1 : 0)};
  animation: ${({ $inView }) => $inView ? fadeInUp : 'none'} 0.6s ease-out;
  animation-delay: ${({ $index }) => $index * 0.1}s;
  animation-fill-mode: both;
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 20px 40px rgba(0, 102, 204, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    border-color: rgba(0, 102, 204, 0.2);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: linear-gradient(135deg, #0066CC 0%, #00A8A8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 102, 204, 0.3);
  transition: all ${({ theme }) => theme.transitions.base};
  
  ${InfoCard}:hover & {
    transform: scale(1.1) rotate(5deg);
    box-shadow: 0 6px 20px rgba(0, 102, 204, 0.4);
  }
`;

const InfoContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const InfoTitle = styled.h4`
  color: #1e293b;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const InfoText = styled.p`
  color: #64748b;
  font-size: ${({ theme }) => theme.fontSizes.base};
  line-height: 1.6;
  margin: 0;
  
  a {
    color: #0066CC;
    text-decoration: none;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    transition: all ${({ theme }) => theme.transitions.fast};
    
    &:hover {
      color: #00A8A8;
      text-decoration: underline;
    }
  }
`;

const HoursGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const DayLabel = styled.span`
  color: #1e293b;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const TimeLabel = styled.span`
  color: #64748b;
`;

export const Contact = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <ContactSection id="contact" ref={ref}>
      <Container>
        <SectionHeader>
          <SectionBadge>Contact Us</SectionBadge>
          <SectionTitle>
            Get In <span>Touch</span>
          </SectionTitle>
          <SectionDescription>
            Ready to revolutionize your revenue cycle? Contact us today
          </SectionDescription>
        </SectionHeader>
        
        <ContentGrid>
          <InfoCard $inView={inView} $index={0}>
            <IconWrapper aria-hidden="true">
              <FaMapMarkerAlt />
            </IconWrapper>
            <InfoContent>
              <InfoTitle>Primary Mailing Address</InfoTitle>
              <InfoText>
                No 20/3 Pandit Nehru 2nd St, Kamaraj Nagar, Avadi, Chennai - 71<br />
                GSTIN: 33FSIPS0954N1Z4
              </InfoText>
            </InfoContent>
          </InfoCard>

          <InfoCard $inView={inView} $index={1}>
            <IconWrapper aria-hidden="true">
              <FaMapMarkerAlt />
            </IconWrapper>
            <InfoContent>
              <InfoTitle>Physical Office</InfoTitle>
              <InfoText>
                42, 5th Cross St, VGN Mahalakshmi Nagar, Perumalagaram, Thiruverkadu, Chennai, 600077<br />
                GSTIN: 33FSIPS0954N1Z4
              </InfoText>
            </InfoContent>
          </InfoCard>

          <InfoCard $inView={inView} $index={2}>
            <IconWrapper aria-hidden="true">
              <FaMapMarkerAlt />
            </IconWrapper>
            <InfoContent>
              <InfoTitle>US Virtual Office</InfoTitle>
              <InfoText>
                16192 Coastal Highway<br />
                Lewes, Delaware 19958<br />
                EIN: 38-4340135 (pending)
              </InfoText>
            </InfoContent>
          </InfoCard>

          <InfoCard $inView={inView} $index={3}>
            <IconWrapper aria-hidden="true">
              <FaPhone />
            </IconWrapper>
            <InfoContent>
              <InfoTitle>Phone</InfoTitle>
              <InfoText>
                Main: <a href="tel:+18187324513">+1 (818) 732-4513</a><br />
                WhatsApp: <a href="https://wa.me/919840832469">+91 98408 32469</a>
              </InfoText>
            </InfoContent>
          </InfoCard>

          <InfoCard $inView={inView} $index={4}>
            <IconWrapper aria-hidden="true">
              <FaEnvelope />
            </IconWrapper>
            <InfoContent>
              <InfoTitle>Email</InfoTitle>
              <InfoText>
                General Inquiries: <a href="mailto:sid@logghealthcare.com">sid@logghealthcare.com</a><br />
                {/* Support: <a href="mailto:sid@logghealthcare.com">sid@logghealthcare.com</a> */}
              </InfoText>
            </InfoContent>
          </InfoCard>

          <InfoCard $inView={inView} $index={5}>
            <IconWrapper aria-hidden="true">
              <FaClock />
            </IconWrapper>
            <InfoContent>
              <InfoTitle>Business Hours</InfoTitle>
              <HoursGrid>
                <DayLabel>Monday - Friday:</DayLabel>
                <TimeLabel>9:00 AM - 6:00 PM EST</TimeLabel>
                <DayLabel>Saturday:</DayLabel>
                <TimeLabel>10:00 AM - 4:00 PM EST</TimeLabel>
                <DayLabel>Sunday:</DayLabel>
                <TimeLabel>Closed</TimeLabel>
                <DayLabel>Support:</DayLabel>
                <TimeLabel>24/7 Email Support</TimeLabel>
              </HoursGrid>
            </InfoContent>
          </InfoCard>
        </ContentGrid>
      </Container>
    </ContactSection>
  );
};
